import { ref, onMounted, onUnmounted, nextTick } from 'vue'

/**
 * useFocusManagement - Composable for accessible focus handling
 * 
 * WCAG Requirements Addressed:
 * - 2.4.3: Focus Order
 * - 2.4.7: Focus Visible
 * - 2.1.2: No Keyboard Trap
 */

// Focusable element selectors
const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(', ')

export function useFocusManagement() {
  const previouslyFocusedElement = ref<HTMLElement | null>(null)

  /**
   * Get all focusable elements within a container
   */
  function getFocusableElements(container: HTMLElement | null): HTMLElement[] {
    if (!container) return []
    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS))
      .filter(el => {
        // Filter out elements that are not visible. visibility erft over,
        // maar display niet — dus ook de voorouders langslopen voor
        // display:none (bv. een knop in een dichtgeklapt paneel).
        if (window.getComputedStyle(el).visibility === 'hidden') return false
        for (let node: HTMLElement | null = el; node; node = node.parentElement) {
          if (window.getComputedStyle(node).display === 'none') return false
        }
        return true
      })
  }

  /**
   * Focus the first focusable element in a container
   */
  function focusFirst(container: HTMLElement | null) {
    if (!container) return
    
    nextTick(() => {
      const focusable = getFocusableElements(container)
      if (focusable.length > 0) {
        focusable[0].focus()
      }
    })
  }

  /**
   * Focus the last focusable element in a container
   */
  function focusLast(container: HTMLElement | null) {
    if (!container) return
    
    nextTick(() => {
      const focusable = getFocusableElements(container)
      if (focusable.length > 0) {
        focusable[focusable.length - 1].focus()
      }
    })
  }

  /**
   * Focus a specific element by selector
   */
  function focusElement(selector: string) {
    nextTick(() => {
      const element = document.querySelector<HTMLElement>(selector)
      if (element) {
        // Ensure element can receive focus
        if (!element.hasAttribute('tabindex')) {
          element.setAttribute('tabindex', '-1')
        }
        element.focus()
      }
    })
  }

  /**
   * Store current focus for later restoration
   */
  function saveFocus() {
    previouslyFocusedElement.value = document.activeElement as HTMLElement
  }

  /**
   * Restore focus to previously focused element
   */
  function restoreFocus() {
    nextTick(() => {
      if (previouslyFocusedElement.value) {
        previouslyFocusedElement.value.focus()
        previouslyFocusedElement.value = null
      }
    })
  }

  /**
   * Move focus to next focusable element
   */
  function focusNext(container: HTMLElement | null) {
    if (!container) return
    
    const focusable = getFocusableElements(container)
    const currentIndex = focusable.indexOf(document.activeElement as HTMLElement)
    
    if (currentIndex < focusable.length - 1) {
      focusable[currentIndex + 1].focus()
    } else {
      // Wrap to first
      focusable[0]?.focus()
    }
  }

  /**
   * Move focus to previous focusable element
   */
  function focusPrevious(container: HTMLElement | null) {
    if (!container) return
    
    const focusable = getFocusableElements(container)
    const currentIndex = focusable.indexOf(document.activeElement as HTMLElement)
    
    if (currentIndex > 0) {
      focusable[currentIndex - 1].focus()
    } else {
      // Wrap to last
      focusable[focusable.length - 1]?.focus()
    }
  }

  /**
   * Check if focus is within a container
   */
  function isFocusWithin(container: HTMLElement | null): boolean {
    if (!container) return false
    return container.contains(document.activeElement)
  }

  /**
   * Set up a focus trap within a container
   * Returns cleanup function
   */
  function trapFocus(container: HTMLElement): () => void {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return

      const focusable = getFocusableElements(container)
      if (focusable.length === 0) return

      const firstElement = focusable[0]
      const lastElement = focusable[focusable.length - 1]

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault()
          lastElement.focus()
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault()
          firstElement.focus()
        }
      }
    }

    container.addEventListener('keydown', handleKeyDown)
    
    return () => {
      container.removeEventListener('keydown', handleKeyDown)
    }
  }

  /**
   * Handle roving tabindex for a group of elements (like toolbar buttons)
   */
  function useRovingTabindex(
    container: HTMLElement | null,
    selector: string
  ) {
    if (!container) return

    const elements = container.querySelectorAll<HTMLElement>(selector)
    if (elements.length === 0) return

    // Set initial tabindex: first element is 0, rest are -1
    elements.forEach((el, index) => {
      el.setAttribute('tabindex', index === 0 ? '0' : '-1')
    })

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement
      if (!target.matches(selector)) return

      const elementsList = Array.from(elements)
      const currentIndex = elementsList.indexOf(target)

      let newIndex = currentIndex

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault()
          newIndex = (currentIndex + 1) % elementsList.length
          break
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault()
          newIndex = (currentIndex - 1 + elementsList.length) % elementsList.length
          break
        case 'Home':
          event.preventDefault()
          newIndex = 0
          break
        case 'End':
          event.preventDefault()
          newIndex = elementsList.length - 1
          break
        default:
          return
      }

      // Update tabindex and focus
      elements.forEach((el, index) => {
        el.setAttribute('tabindex', index === newIndex ? '0' : '-1')
      })
      elementsList[newIndex].focus()
    }

    container.addEventListener('keydown', handleKeyDown)

    return () => {
      container.removeEventListener('keydown', handleKeyDown)
    }
  }

  return {
    getFocusableElements,
    focusFirst,
    focusLast,
    focusElement,
    saveFocus,
    restoreFocus,
    focusNext,
    focusPrevious,
    isFocusWithin,
    trapFocus,
    useRovingTabindex,
    previouslyFocusedElement,
  }
}
